import React from 'react';
import { Field, Section, Toggle } from './shared';

export const STANDARD_EVENTS = ['CompleteRegistration', 'Schedule', 'Purchase', 'SubmitApplication', 'Contact', 'Lead'];
const PIXEL_RE = /^\d{6,20}$/;
const EVENT_RE = /^[A-Za-z0-9_]{1,40}$/;

// Options a rule may match against: the question's own choices (yes/no has fixed labels).
const optionsOf = (q) => (q?.type === 'yes_no' ? ['Sim', 'Não'] : (q?.options || []));

const isEmptyPixel = (mp) => !mp || (
  !(mp.pixelId || '').trim() && !mp.leadEvent?.enabled && !mp.conversionEvent?.enabled
);

// Returns the metaPixel to send: rules of deleted questions dropped; undefined when everything is off.
export function normalizeMetaPixel(schema) {
  const mp = schema.metaPixel;
  if (isEmptyPixel(mp)) return undefined;
  const ids = new Set((schema.questions || []).map((q) => q.id));
  const out = { pixelId: (mp.pixelId || '').trim() };
  if (mp.leadEvent) out.leadEvent = { enabled: !!mp.leadEvent.enabled, trigger: mp.leadEvent.trigger === 'submit' ? 'submit' : 'contact' };
  if (mp.conversionEvent) {
    out.conversionEvent = {
      enabled: !!mp.conversionEvent.enabled,
      eventName: (mp.conversionEvent.eventName || '').trim(),
      rules: (mp.conversionEvent.rules || []).filter((r) => ids.has(r.questionId)),
    };
  }
  return out;
}

export function validateMetaPixel(schema) {
  const mp = normalizeMetaPixel(schema);
  if (!mp) return [];
  const errors = [];
  if (!mp.pixelId) errors.push('Rastreamento: informe o ID do pixel para ativar os eventos.');
  else if (!PIXEL_RE.test(mp.pixelId)) errors.push('Rastreamento: o ID do pixel deve ter de 6 a 20 dígitos.');
  const c = mp.conversionEvent;
  if (c?.enabled) {
    if (!EVENT_RE.test(c.eventName)) errors.push('Rastreamento: nome do evento inválido (letras, números e _, até 40).');
    if (c.rules.length === 0) errors.push('Rastreamento: adicione ao menos uma regra para o evento de conversão.');
    if (c.rules.some((r) => !String(r.equals ?? '').trim())) errors.push('Rastreamento: preencha o valor de todas as regras.');
  }
  return errors;
}

export default function TrackingTab({ schema, onChange }) {
  const mp = schema.metaPixel || {};
  const lead = mp.leadEvent || { enabled: false, trigger: 'contact' };
  const conv = mp.conversionEvent || { enabled: false, eventName: 'CompleteRegistration', rules: [] };
  const questions = (schema.questions || []).filter((q) => q.type !== 'notice');
  const qIds = new Set((schema.questions || []).map((q) => q.id));

  const set = (patch) => onChange({ ...schema, metaPixel: { ...mp, ...patch } });
  const setLead = (patch) => set({ leadEvent: { ...lead, ...patch } });
  const setConv = (patch) => set({ conversionEvent: { ...conv, ...patch } });

  const pixelId = mp.pixelId || '';
  const pixelBad = pixelId.trim() !== '' && !PIXEL_RE.test(pixelId.trim());
  const isStd = STANDARD_EVENTS.includes(conv.eventName);
  const rules = conv.rules || [];
  const orphans = rules.filter((r) => !qIds.has(r.questionId)).length;
  const updateRule = (i, patch) => setConv({ rules: rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-surface-container-low p-4 text-sm text-slate-600 flex gap-2">
        <span className="material-symbols-outlined text-[18px] text-primary">info</span>
        <p>
          O pixel só carrega na página pública, nunca na prévia. Se o consentimento estiver obrigatório (aba Abandono), nenhum evento é enviado antes do aceite.
        </p>
      </div>

      <Section title="Pixel da Meta">
        <Field
          label="ID do pixel"
          hint="Encontre no Gerenciador de Eventos da Meta: Fontes de dados > selecione o pixel > o ID aparece abaixo do nome. Deixe vazio para desligar o rastreamento."
        >
          <input
            className="input max-w-[320px]" inputMode="numeric" placeholder="123456789012345" maxLength={20}
            value={pixelId} onChange={(e) => set({ pixelId: e.target.value.replace(/\s/g, '') })}
          />
          {pixelBad && <p className="text-xs text-red-600 mt-1">O ID do pixel deve ter de 6 a 20 dígitos.</p>}
        </Field>
      </Section>

      <Section title="Evento Lead">
        <Toggle checked={lead.enabled} onChange={(v) => setLead({ enabled: v })} label="Enviar evento Lead" />
        {lead.enabled && (
          <Field label="Quando disparar" hint="Uma vez por sessão.">
            <select className="input max-w-[360px]" value={lead.trigger === 'submit' ? 'submit' : 'contact'} onChange={(e) => setLead({ trigger: e.target.value })}>
              <option value="contact">Ao informar telefone ou e-mail (já no parcial)</option>
              <option value="submit">No envio do formulário</option>
            </select>
          </Field>
        )}
      </Section>

      <Section title="Evento de conversão">
        <Toggle checked={conv.enabled} onChange={(v) => setConv({ enabled: v })} label="Enviar evento de conversão no envio" />
        {conv.enabled && (
          <>
            <p className="text-xs text-slate-500 -mt-2">Disparado no envio quando qualquer uma das regras abaixo combinar com as respostas.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Evento">
                <select className="input" value={isStd ? conv.eventName : 'custom'}
                  onChange={(e) => setConv({ eventName: e.target.value === 'custom' ? '' : e.target.value })}>
                  {STANDARD_EVENTS.map((n) => <option key={n} value={n}>{n}</option>)}
                  <option value="custom">Personalizado...</option>
                </select>
              </Field>
              {!isStd && (
                <Field label="Nome do evento" hint="Letras, números e _ (até 40).">
                  <input className="input" maxLength={40} value={conv.eventName || ''}
                    onChange={(e) => setConv({ eventName: e.target.value.replace(/[^A-Za-z0-9_]/g, '') })} />
                </Field>
              )}
            </div>

            <div className="space-y-3">
              <p className="text-xs font-bold text-secondary uppercase tracking-wider">Regras (qualquer uma)</p>
              {orphans > 0 && (
                <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-lg p-2">
                  {orphans} regra(s) apontam para perguntas removidas e serão descartadas ao salvar.
                </p>
              )}
              {rules.map((r, i) => {
                const q = (schema.questions || []).find((x) => x.id === r.questionId);
                const opts = optionsOf(q);
                if (!q) return null;
                return (
                  <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end border border-slate-100 rounded-xl p-3">
                    <Field label="Pergunta">
                      <select className="input" value={r.questionId} onChange={(e) => updateRule(i, { questionId: e.target.value, equals: '' })}>
                        {questions.map((x) => <option key={x.id} value={x.id}>{x.label || x.id}</option>)}
                      </select>
                    </Field>
                    <Field label="For igual a">
                      {opts.length > 0 ? (
                        <select className="input" value={r.equals ?? ''} onChange={(e) => updateRule(i, { equals: e.target.value })}>
                          <option value="">Selecione...</option>
                          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input className="input" maxLength={200} value={r.equals ?? ''} onChange={(e) => updateRule(i, { equals: e.target.value })} />
                      )}
                    </Field>
                    <button type="button" className="p-2 rounded text-red-500 hover:bg-red-50 justify-self-end" title="Remover regra" aria-label="Remover regra"
                      onClick={() => setConv({ rules: rules.filter((_, j) => j !== i) })}>
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                );
              })}
              <button type="button" className="btn btn-secondary flex items-center gap-2" disabled={questions.length === 0}
                onClick={() => setConv({ rules: [...rules, { questionId: questions[0]?.id || '', equals: '' }] })}>
                <span className="material-symbols-outlined text-[18px]">add</span>
                Adicionar regra
              </button>
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
